// Faq.js
import React from 'react';

function Faq() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'teacher';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-xl font-bold mb-2">FAQ – VisitOMS EasyCare</h1>
      <p className="text-sm text-gray-600 mb-4">
        Here you can find answers to the most common questions about the VisitOMS EasyCare
        platform, both for administrators and for teachers/providers.
      </p>

      {/* GENERAL */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">General</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is VisitOMS EasyCare?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            VisitOMS EasyCare is a platform for managing home-based visits for children aged
            0–36 months in EI and PI programs. It integrates scheduling, routing, time logging,
            skill tracking, and reporting so that agencies and providers can focus on the child,
            not on paperwork.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Who uses the system?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            The system is used by:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>Agency administrators and coordinators (Admin role).</li>
            <li>
              Teachers / providers / specialists who visit children and work with families
              (Teacher role).
            </li>
          </ul>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What do EI and PI programs mean in this context?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            EI stands for Early Intervention (0–36 months), and PI stands for Project
            Impact (0–36 months). The system supports both and allows program
            type to be stored per child, so reports and planning can differentiate EI vs PI.
          </p>
        </details>
      </section>

      {/* ADMIN SECTION */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">For administrators</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I add or edit teachers?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Use <span className="font-medium">Manage Teachers</span> in the admin menu. There you
            can create new teachers, set their contact details, address, availability rules,
            and internal notes. Changes are immediately available in scheduling modules.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I register a new child and connect parents?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Go to <span className="font-medium">Manage Students</span>. You can enter child
            demographics, program type (EI/PI), addresses (primary and alternative), parent
            information, and default teacher. This data is used later for scheduling, routing,
            and reports.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I schedule visits for a child?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Use <span className="font-medium">Manage Visits</span> for weekly planning of one-hour
            slots per child. The system suggests the default teacher when available and can
            propose substitute teachers based on availability, distance, and cost. You can also
            use <span className="font-medium">Smart Visits Scheduler</span> when you need more
            automated planning.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is the difference between a regular visit and a PI / Event visit?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Regular visits are usually one-hour EI visits. PI &amp; Event visits are multi-hour
            sessions (for Project Impact or special events) and have an explicit end time.
            They are treated differently in time logs and cost calculations and are highlighted
            as PI/Event in the interface.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How is availability handled for teachers?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Teachers enter their weekly availability and exceptions in{' '}
            <span className="font-medium">My Availability</span>. As an admin, you can review and
            adjust it in <span className="font-medium">Teachers Availability</span>. Scheduling
            uses this data to avoid double-booking and to respect day-off rules and exceptions.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Where can I see work reports and time logs?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Use <span className="font-medium">Teachers Work Report</span> and{' '}
            <span className="font-medium">Teacher Time Logs</span>. Here you can filter by date
            range, teacher, and program type, and export summaries for billing and payroll.
          </p>
        </details>
      </section>

      {/* TEACHER SECTION */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">For teachers / providers</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What should I do first when I log in?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            First check <span className="font-medium">My Day Planner</span> and{' '}
            <span className="font-medium">My Visits</span> to see your schedule for the day.
            Make sure geolocation permissions are enabled on your device, since arrival and
            check-in/out use GPS.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I record arrival, check-in, and check-out?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            When you reach a child&apos;s location, use the buttons in{' '}
            <span className="font-medium">My Visits</span> or the visit detail view:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 mt-1">
            <li>
              <span className="font-medium">Arrival</span> – confirms that you are near the
              child&apos;s address.
            </li>
            <li>
              <span className="font-medium">Check-in</span> – starts the visit, with timestamp
              and geolocation.
            </li>
            <li>
              <span className="font-medium">Check-out</span> – ends the visit, again with time
              and location.
            </li>
          </ul>
          <p className="text-sm text-gray-700 mt-1">
            These actions are used to generate objective time logs and route information.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I update child skills and goals?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Use <span className="font-medium">My Students Skills</span> to see domains and skills
            for each child. You can set baseline status, update progress, and review history. Use{' '}
            <span className="font-medium">Student Goals &amp; Achievement</span> to summarize
            goals and achievements for reporting periods.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Where can I see my hours and routes?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Go to <span className="font-medium">My Time Log</span>. You can review your visits,
            drive time, breaks (where applicable), and event sessions for a selected date range.
            This module is used by the agency to calculate compensation and verify workload.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What if my phone does not allow geolocation?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Make sure you allow location access for the browser or app. If you previously denied
            it, go to your phone settings and re-enable location for this app. If GPS is still
            not available (e.g. indoor, no signal), you can continue working, but some automatic
            route and time calculations may fall back to scheduled times instead of objective
            GPS-based data.
          </p>
        </details>
      </section>

      {/* Small note depending on role */}
      <p className="text-xs text-gray-500 text-center mt-4">
        You are currently logged in as: <span className="font-medium">{role}</span>.
        Some screenshots or labels may differ slightly depending on your agency configuration.
      </p>
    </div>
  );
}

export default Faq;
